import { Component, ChangeDetectionStrategy, signal, computed, effect, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './app.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnDestroy {
  audioContext: AudioContext;
  currentSource: AudioBufferSourceNode | null = null;
  previewSource: AudioBufferSourceNode | null = null;
  animationFrameId: number | null = null;

  audioBuffer = signal<AudioBuffer | null>(null);
  isPlaying = signal(false);
  isSeeking = signal(false); // To prevent progress updates while user drags slider
  currentTime = signal(0);
  duration = computed(() => this.audioBuffer()?.duration ?? 0);
  startTime = signal(0);
  endTime = signal(0);
  isLoading = signal(false);
  errorMessage = signal<string | null>(null);
  filename = signal<string | null>(null);
  version = signal('1.0.6'); // Incremented version for independent concat file uploads

  // Fine-tuning time signals
  startMinutes = signal(0);
  startSeconds = signal(0);
  startMilliseconds = signal(0);
  endMinutes = signal(0);
  endSeconds = signal(0);
  endMilliseconds = signal(0);

  // Concatenation specific signals (fully decoupled)
  audioBufferConcat1 = signal<AudioBuffer | null>(null);
  filenameConcat1 = signal<string | null>(null);
  audioBufferConcat2 = signal<AudioBuffer | null>(null);
  filenameConcat2 = signal<string | null>(null);
  isConcatenating = signal(false);
  concatenateErrorMessage = signal<string | null>(null);

  private playbackStartTime: number = 0; // AudioContext.currentTime when current playback started

  constructor() {
    this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

    // Effect to update fine-tuning inputs when startTime or endTime changes
    effect(() => {
      this.updateTimeInputs();
    });

    // Effect to set initial endTime to duration when audioBuffer is loaded
    effect(() => {
      const duration = this.duration();
      if (duration > 0 && this.endTime() === 0) {
        this.endTime.set(duration);
      }
    });
  }

  ngOnDestroy(): void {
    this.stop();
    // Stop any concatenation-related playback if it exists
    if (this.previewSource) {
      this.previewSource.stop();
      this.previewSource.disconnect();
    }
    this.audioContext.close();
  }

  // File Loading (Primary for Trimming)
  async handleFileInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    const fileType = file.type;
    const fileName = file.name.toLowerCase();
    if (!(fileType.startsWith('audio/mp3') || fileType.startsWith('audio/mpeg') || fileType.startsWith('audio/wav') || fileType.startsWith('audio/mp4') || fileType.startsWith('audio/x-m4a') || fileType.startsWith('audio/m4a') || fileName.endsWith('.m4a'))) {
      this.errorMessage.set('Please upload an MP3, WAV, or M4A audio file.');
      this.isLoading.set(false);
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.stop(); // Stop any current playback
    this.resetPlayerState(); // Reset player state

    try {
      this.filename.set(file.name);
      const arrayBuffer = await file.arrayBuffer();
      const decodedBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      this.audioBuffer.set(decodedBuffer);
      this.endTime.set(decodedBuffer.duration); // Set end time to full duration initially
    } catch (error) {
      console.error('Error decoding audio:', error);
      this.errorMessage.set('Failed to decode audio. Please ensure it\'s a valid MP3, WAV, or M4A file.');
      this.audioBuffer.set(null);
      this.filename.set(null);
    } finally {
      this.isLoading.set(false);
    }
  }

  // File Loading for Concatenation - First File
  async handleFileInputConcat1(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    const fileType = file.type;
    const fileName = file.name.toLowerCase();
    if (!(fileType.startsWith('audio/mp3') || fileType.startsWith('audio/mpeg') || fileType.startsWith('audio/wav') || fileType.startsWith('audio/mp4') || fileType.startsWith('audio/x-m4a') || fileType.startsWith('audio/m4a') || fileName.endsWith('.m4a'))) {
      this.concatenateErrorMessage.set('Please upload an MP3, WAV, or M4A audio file for the first concatenation audio.');
      return;
    }

    this.isConcatenating.set(true);
    this.concatenateErrorMessage.set(null);

    try {
      this.filenameConcat1.set(file.name);
      const arrayBuffer = await file.arrayBuffer();
      const decodedBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      this.audioBufferConcat1.set(decodedBuffer);
    } catch (error) {
      console.error('Error decoding first concatenation audio:', error);
      this.concatenateErrorMessage.set('Failed to decode first concatenation audio. Please ensure it\'s a valid MP3, WAV, or M4A file.');
      this.audioBufferConcat1.set(null);
      this.filenameConcat1.set(null);
    } finally {
      this.isConcatenating.set(false);
    }
  }

  // File Loading for Concatenation - Second File
  async handleFileInputConcat2(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    const fileType = file.type;
    const fileName = file.name.toLowerCase();
    if (!(fileType.startsWith('audio/mp3') || fileType.startsWith('audio/mpeg') || fileType.startsWith('audio/wav') || fileType.startsWith('audio/mp4') || fileType.startsWith('audio/x-m4a') || fileType.startsWith('audio/m4a') || fileName.endsWith('.m4a'))) {
      this.concatenateErrorMessage.set('Please upload an MP3, WAV, or M4A audio file for the second concatenation audio.');
      return;
    }

    this.isConcatenating.set(true); // Use concatenation loading for this
    this.concatenateErrorMessage.set(null);

    try {
      this.filenameConcat2.set(file.name);
      const arrayBuffer = await file.arrayBuffer();
      const decodedBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      this.audioBufferConcat2.set(decodedBuffer);
    } catch (error) {
      console.error('Error decoding second concatenation audio:', error);
      this.concatenateErrorMessage.set('Failed to decode second concatenation audio. Please ensure it\'s a valid MP3, WAV, or M4A file.');
      this.audioBufferConcat2.set(null);
      this.filenameConcat2.set(null);
    } finally {
      this.isConcatenating.set(false);
    }
  }


  private resetPlayerState() {
    this.isPlaying.set(false);
    this.currentTime.set(0);
    this.startTime.set(0);
    this.endTime.set(0);
    // this.audioBuffer.set(null); // Keep this to allow re-uploading the same file, or clear for full reset
  }

  // Audio Playback Controls
  play() {
    if (!this.audioBuffer() || this.isPlaying()) {
      return;
    }

    this.audioContext.resume().then(() => {
      this.currentSource = this.audioContext.createBufferSource();
      this.currentSource.buffer = this.audioBuffer();
      this.currentSource.connect(this.audioContext.destination);

      const offset = this.currentTime();
      const durationRemaining = this.duration() - offset;

      this.currentSource.start(0, offset, durationRemaining);
      this.isPlaying.set(true);
      this.playbackStartTime = this.audioContext.currentTime - offset;

      this.currentSource.onended = () => {
        // Only stop if not paused manually
        if (this.isPlaying()) {
          this.stop();
        }
      };
      this.updateProgressLoop();
    });
  }

  pause() {
    if (this.currentSource) {
      this.currentSource.stop();
      this.currentSource.disconnect();
      this.currentSource = null;
    }
    this.isPlaying.set(false);
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  stop() {
    this.pause();
    this.currentTime.set(0);
  }

  restart() {
    this.stop();
    this.play();
  }

  // Progress and Seeking
  private updateProgressLoop() {
    if (this.isPlaying() && this.currentSource && !this.isSeeking()) {
      const elapsed = this.audioContext.currentTime - this.playbackStartTime;
      const newCurrentTime = Math.min(this.duration(), Math.max(0, elapsed)); // Clamp to duration bounds
      this.currentTime.set(newCurrentTime);

      if (newCurrentTime >= this.duration()) {
        this.stop();
      } else {
        this.animationFrameId = requestAnimationFrame(() => this.updateProgressLoop());
      }
    } else if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  seek(event: Event) {
    const input = event.target as HTMLInputElement;
    const seekTime = parseFloat(input.value);
    if (isNaN(seekTime)) {
      return;
    }

    this.currentTime.set(seekTime);
    this.pause(); // Pause current playback
    this.playbackStartTime = this.audioContext.currentTime - seekTime; // Reset playback start time for accurate resume
    // The play() function will start from the new currentTime
  }

  startSeek() {
    this.isSeeking.set(true);
  }

  endSeek() {
    this.isSeeking.set(false);
    if (this.isPlaying()) {
      this.play(); // Resume playback from new position
    }
  }

  // Trimming Workflow
  setStartTime() {
    this.startTime.set(this.currentTime());
  }

  setEndTime() {
    this.endTime.set(this.currentTime());
  }

  // Helper to convert seconds to M:S:MS
  private getTimeParts(timeInSeconds: number) {
    const totalMilliseconds = Math.floor(timeInSeconds * 1000);
    const minutes = Math.floor(totalMilliseconds / (1000 * 60));
    const seconds = Math.floor((totalMilliseconds % (1000 * 60)) / 1000);
    const milliseconds = totalMilliseconds % 1000;
    return { minutes, seconds, milliseconds };
  }

  // Helper to convert M:S:MS to seconds
  private convertToSeconds(minutes: number, seconds: number, milliseconds: number): number {
    return minutes * 60 + seconds + milliseconds / 1000;
  }

  // Update fine-tuning input signals from startTime/endTime
  private updateTimeInputs() {
    const { minutes: sm, seconds: ss, milliseconds: sms } = this.getTimeParts(this.startTime());
    this.startMinutes.set(sm);
    this.startSeconds.set(ss);
    this.startMilliseconds.set(sms);

    const { minutes: em, seconds: es, milliseconds: ems } = this.getTimeParts(this.endTime());
    this.endMinutes.set(em);
    this.endSeconds.set(es);
    this.endMilliseconds.set(ems);
  }

  // Update startTime from fine-tuning inputs
  updateStartTimeFromInputs() {
    const newStartTime = this.convertToSeconds(
      this.startMinutes(),
      this.startSeconds(),
      this.startMilliseconds()
    );
    // Ensure newStartTime is valid (non-negative, not past endTime, not past duration)
    if (newStartTime >= 0 && newStartTime < this.endTime() && newStartTime <= this.duration()) {
      this.startTime.set(newStartTime);
    } else {
      // Revert inputs to previous valid state if invalid input
      this.updateTimeInputs();
    }
  }

  // Update endTime from fine-tuning inputs
  updateEndTimeFromInputs() {
    const newEndTime = this.convertToSeconds(
      this.endMinutes(),
      this.endSeconds(),
      this.endMilliseconds()
    );
    // Ensure newEndTime is valid (after startTime, not past duration)
    if (newEndTime > this.startTime() && newEndTime <= this.duration()) {
      this.endTime.set(newEndTime);
    } else {
      // Revert inputs to previous valid state if invalid input
      this.updateTimeInputs();
    }
  }

  // Preview & Export
  previewSegment() {
    if (!this.audioBuffer() || this.startTime() >= this.endTime()) {
      this.errorMessage.set('Invalid segment for preview.');
      return;
    }

    this.stop(); // Stop any current playback

    this.audioContext.resume().then(() => {
      if (this.previewSource) {
        this.previewSource.stop();
        this.previewSource.disconnect();
      }

      this.previewSource = this.audioContext.createBufferSource();
      this.previewSource.buffer = this.audioBuffer();
      this.previewSource.connect(this.audioContext.destination);

      const offset = this.startTime();
      const duration = this.endTime() - this.startTime();

      this.previewSource.start(0, offset, duration);
      this.isPlaying.set(true); // Indicate playing
      this.currentTime.set(offset); // Set current time to start of preview

      const previewStartTimeContext = this.audioContext.currentTime;

      const updatePreviewProgress = () => {
        if (this.isPlaying()) {
          const elapsed = this.audioContext.currentTime - previewStartTimeContext;
          this.currentTime.set(Math.min(offset + elapsed, this.endTime()));
          if (this.currentTime() < this.endTime()) {
            this.animationFrameId = requestAnimationFrame(updatePreviewProgress);
          } else {
            this.stop();
          }
        }
      };
      this.animationFrameId = requestAnimationFrame(updatePreviewProgress);

      this.previewSource.onended = () => {
        if (this.isPlaying()) { // Check if it wasn't manually stopped
          this.stop();
        }
      };
    });
  }


  async saveTrimmedAudio() {
    const audioBuffer = this.audioBuffer();
    if (!audioBuffer) {
      this.errorMessage.set('No audio file loaded.');
      return;
    }

    const startOffsetSeconds = this.startTime();
    const endOffsetSeconds = this.endTime();

    if (startOffsetSeconds >= endOffsetSeconds) {
      this.errorMessage.set('Invalid trim segment. End time must be after start time.');
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    try {
      const sampleRate = audioBuffer.sampleRate;
      const numberOfChannels = audioBuffer.numberOfChannels;

      const startSample = Math.floor(startOffsetSeconds * sampleRate);
      const endSample = Math.floor(endOffsetSeconds * sampleRate);
      const segmentLengthSamples = endSample - startSample;

      // Create a buffer for all interleaved samples
      const interleaved = new Int16Array(segmentLengthSamples * numberOfChannels);

      for (let channel = 0; channel < numberOfChannels; channel++) {
        const channelData = audioBuffer.getChannelData(channel).subarray(startSample, endSample);
        const int16ChannelData = this.float32ToInt16(channelData);

        // Interleave channels
        for (let i = 0; i < segmentLengthSamples; i++) {
          interleaved[i * numberOfChannels + channel] = int16ChannelData[i];
        }
      }

      const dataView = new DataView(new ArrayBuffer(44 + interleaved.byteLength));

      // Write WAV header
      // RIFF chunk descriptor
      this.writeString(dataView, 0, 'RIFF'); // ChunkID
      dataView.setUint32(4, 36 + interleaved.byteLength, true); // ChunkSize
      this.writeString(dataView, 8, 'WAVE'); // Format

      // FMT sub-chunk
      this.writeString(dataView, 12, 'fmt '); // Subchunk1ID
      dataView.setUint32(16, 16, true); // Subchunk1Size (16 for PCM)
      dataView.setUint16(20, 1, true); // AudioFormat (1 for PCM)
      dataView.setUint16(22, numberOfChannels, true); // NumChannels
      dataView.setUint32(24, sampleRate, true); // SampleRate
      dataView.setUint32(28, sampleRate * numberOfChannels * 2, true); // ByteRate (SampleRate * NumChannels * BitsPerSample/8)
      dataView.setUint16(32, numberOfChannels * 2, true); // BlockAlign (NumChannels * BitsPerSample/8)
      dataView.setUint16(34, 16, true); // BitsPerSample

      // DATA sub-chunk
      this.writeString(dataView, 36, 'data'); // Subchunk2ID
      dataView.setUint32(40, interleaved.byteLength, true); // Subchunk2Size (NumSamples * NumChannels * BitsPerSample/8)

      // Write the interleaved audio data
      for (let i = 0; i < interleaved.length; i++) {
        dataView.setInt16(44 + i * 2, interleaved[i], true);
      }

      const blob = new Blob([dataView], { type: 'audio/wav' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `trimmed_${this.filename()?.replace(/\.[^/.]+$/, '') || 'audio'}.wav`; // Ensure .wav extension
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url); // Clean up

    } catch (error) {
      console.error('Error encoding or saving audio:', error);
      this.errorMessage.set(`Failed to save the WAV file: ${(error as Error).message || 'Unknown error'}`);
    } finally {
      this.isLoading.set(false);
    }
  }

  async concatenateAudioFiles() {
    const buffer1 = this.audioBufferConcat1();
    const buffer2 = this.audioBufferConcat2();

    if (!buffer1 || !buffer2) {
      this.concatenateErrorMessage.set('Please load both audio files for concatenation.');
      return;
    }

    if (buffer1.sampleRate !== buffer2.sampleRate) {
      this.concatenateErrorMessage.set('Cannot concatenate: Audio files must have the same sample rate.');
      return;
    }

    if (buffer1.numberOfChannels !== buffer2.numberOfChannels) {
      this.concatenateErrorMessage.set('Cannot concatenate: Audio files must have the same number of channels.');
      return;
    }

    this.isConcatenating.set(true);
    this.concatenateErrorMessage.set(null);

    try {
      const sampleRate = buffer1.sampleRate;
      const numberOfChannels = buffer1.numberOfChannels;
      const combinedLengthSamples = buffer1.length + buffer2.length;

      const combinedBuffer = this.audioContext.createBuffer(
        numberOfChannels,
        combinedLengthSamples,
        sampleRate
      );

      for (let channel = 0; channel < numberOfChannels; channel++) {
        const outputChannel = combinedBuffer.getChannelData(channel);
        const channelData1 = buffer1.getChannelData(channel);
        const channelData2 = buffer2.getChannelData(channel);

        // Copy first audio buffer
        outputChannel.set(channelData1, 0);
        // Copy second audio buffer after the first
        outputChannel.set(channelData2, buffer1.length);
      }

      // Now, convert combinedBuffer to WAV
      const interleaved = new Int16Array(combinedLengthSamples * numberOfChannels);

      for (let channel = 0; channel < numberOfChannels; channel++) {
        const channelData = combinedBuffer.getChannelData(channel);
        const int16ChannelData = this.float32ToInt16(channelData);

        for (let i = 0; i < combinedLengthSamples; i++) {
          interleaved[i * numberOfChannels + channel] = int16ChannelData[i];
        }
      }

      const dataView = new DataView(new ArrayBuffer(44 + interleaved.byteLength));

      // Write WAV header for the concatenated file
      this.writeString(dataView, 0, 'RIFF'); // ChunkID
      dataView.setUint32(4, 36 + interleaved.byteLength, true); // ChunkSize
      this.writeString(dataView, 8, 'WAVE'); // Format

      // FMT sub-chunk
      this.writeString(dataView, 12, 'fmt '); // Subchunk1ID
      dataView.setUint32(16, 16, true); // Subchunk1Size (16 for PCM)
      dataView.setUint16(20, 1, true); // AudioFormat (1 for PCM)
      dataView.setUint16(22, numberOfChannels, true); // NumChannels
      dataView.setUint32(24, sampleRate, true); // SampleRate
      dataView.setUint32(28, sampleRate * numberOfChannels * 2, true); // ByteRate (SampleRate * NumChannels * BitsPerSample/8)
      dataView.setUint16(32, numberOfChannels * 2, true); // BlockAlign (NumChannels * BitsPerSample/8)
      dataView.setUint16(34, 16, true); // BitsPerSample

      // DATA sub-chunk
      this.writeString(dataView, 36, 'data'); // Subchunk2ID
      dataView.setUint32(40, interleaved.byteLength, true); // Subchunk2Size (NumSamples * NumChannels * BitsPerSample/8)

      // Write the interleaved audio data
      for (let i = 0; i < interleaved.length; i++) {
        dataView.setInt16(44 + i * 2, interleaved[i], true);
      }

      const blob = new Blob([dataView], { type: 'audio/wav' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `concatenated_${this.filenameConcat1()?.replace(/\.[^/.]+$/, '') || 'audio1'}_${this.filenameConcat2()?.replace(/\.[^/.]+$/, '') || 'audio2'}.wav`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error concatenating or saving audio:', error);
      this.concatenateErrorMessage.set(`Failed to concatenate and save WAV: ${(error as Error).message || 'Unknown error'}`);
    } finally {
      this.isConcatenating.set(false);
    }
  }


  private writeString(view: DataView, offset: number, s: string) {
    for (let i = 0; i < s.length; i++) {
      view.setUint8(offset + i, s.charCodeAt(i));
    }
  }

  // Helper to convert Float32Array to Int16Array for WAV
  private float32ToInt16(buffer: Float32Array): Int16Array {
    const l = buffer.length;
    const buf = new Int16Array(l);
    const outputScale = 32767; // Max value for Int16Array

    for (let i = 0; i < l; i++) {
      let s = buffer[i];
      // Clamp to -1.0 to 1.0 range
      s = Math.max(-1, Math.min(1, s));
      // Scale and round to the nearest integer
      buf[i] = Math.round(s * outputScale);
    }
    return buf;
  }

  // Format time for display
  formatTime(seconds: number): string {
    if (isNaN(seconds) || seconds < 0) {
      return '00:00:000';
    }
    const { minutes, seconds: secs, milliseconds } = this.getTimeParts(seconds);
    const paddedMinutes = String(minutes).padStart(2, '0');
    const paddedSeconds = String(secs).padStart(2, '0');
    const paddedMilliseconds = String(milliseconds).padStart(3, '0');
    return `${paddedMinutes}:${paddedSeconds}:${paddedMilliseconds}`;
  }
}